import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import AudioDebugger from './AudioDebugger';

const MusicContainer = styled(motion.div)`
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 12px;
    padding: 1rem;
    color: white;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    min-width: 280px;
`;

const ControlsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const PlayButton = styled.button`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    border-radius: 50%;
    width: 45px;
    height: 45px;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    font-size: 16px;
    
    &:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }
`;

const TrackInfo = styled.div`
    flex: 1;
    overflow: hidden;
`;

const TrackTitle = styled.div`
    font-size: 0.9rem;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 2px;
`;

const TrackArtist = styled.div`
    font-size: 0.8rem;
    color: #94a3b8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const VolumeContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 80px;
`;

const VolumeSlider = styled.input`
    width: 60px;
    height: 4px;
    background: #333;
    border-radius: 2px;
    outline: none;
    -webkit-appearance: none;
    
    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 12px;
        height: 12px;
        background: #6366f1;
        border-radius: 50%;
        cursor: pointer;
    }
    
    &::-moz-range-thumb {
        width: 12px;
        height: 12px;
        background: #6366f1;
        border-radius: 50%;
        cursor: pointer;
        border: none;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    font-size: 14px;
    
    &:hover {
        color: white;
        background: rgba(255, 255, 255, 0.1);
    }
`;

const NoticeText = styled.div`
    font-size: 0.7rem;
    color: #64748b;
    margin-top: 0.5rem;
    text-align: center;
    line-height: 1.2;
`;

const BackgroundMusic = () => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(30);
    const [isVisible, setIsVisible] = useState(true);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [audioError, setAudioError] = useState(false);
    const [showDebugger, setShowDebugger] = useState(false);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume / 100;
            audioRef.current.loop = true;
        }
    }, [volume]);

    useEffect(() => {
        // Test if audio file is accessible
        const testAudioAccess = async () => {
            try {
                const response = await fetch('/audio/without-me.mp3', { method: 'HEAD' });
                console.log('Audio file fetch test:', response.status, response.statusText);
                if (response.ok) {
                    console.log('✅ Audio file is accessible');
                } else {
                    console.log('❌ Audio file not accessible:', response.status);
                }
            } catch (error) {
                console.log('❌ Error testing audio file access:', error);
            }
        };
        
        testAudioAccess();
    }, []);

    useEffect(() => {
        // Set up user interaction detection
        const handleFirstInteraction = () => {
            setHasUserInteracted(true);
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };

        document.addEventListener('click', handleFirstInteraction);
        document.addEventListener('keydown', handleFirstInteraction);

        return () => {
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };
    }, []);

    useEffect(() => {
        // Developer shortcut: Ctrl+Shift+D to toggle debugger
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                e.preventDefault();
                setShowDebugger(prev => !prev);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const togglePlay = async () => {
        if (!hasUserInteracted) return;
        
        try {
            if (isPlaying) {
                await audioRef.current?.pause();
                setIsPlaying(false);
            } else {
                await audioRef.current?.play();
                setIsPlaying(true);
                setAudioError(false);
            }
        } catch (error) {
            console.error('Error playing audio:', error);
            setAudioError(true);
            setIsPlaying(false);
        }
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseInt(e.target.value);
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume / 100;
        }
    };

    const handleClose = () => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setIsVisible(false);
        setIsPlaying(false);
    };

    const handleAudioError = (e) => {
        console.error('Audio error occurred:', e);
        console.log('Audio element:', audioRef.current);
        console.log('Audio readyState:', audioRef.current?.readyState);
        console.log('Audio networkState:', audioRef.current?.networkState);
        setAudioError(true);
        setIsPlaying(false);
    };

    const handleAudioLoad = () => {
        console.log('Audio loaded successfully');
        setAudioError(false);
    };

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            <MusicContainer
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.5 }}
            >
                <audio
                    ref={audioRef}
                    preload="metadata"
                    onError={handleAudioError}
                    onLoadedData={handleAudioLoad}
                    onCanPlayThrough={() => console.log('Audio can play through')}
                    onEnded={() => setIsPlaying(false)}
                >
                    {/* Local audio file - should be the primary source */}
                    <source src="/audio/without-me.mp3" type="audio/mpeg" />
                    {/* Fallback: you can add other sources here if needed */}
                    Your browser does not support the audio element.
                </audio>
                
                <ControlsContainer>
                    <PlayButton 
                        onClick={togglePlay}
                        disabled={!hasUserInteracted || audioError}
                        title={
                            !hasUserInteracted 
                                ? 'Click anywhere to enable audio' 
                                : audioError 
                                ? 'Audio file not found' 
                                : (isPlaying ? 'Pause' : 'Play')
                        }
                    >
                        {audioError ? '❌' : (isPlaying ? '⏸️' : '▶️')}
                    </PlayButton>
                    
                    <TrackInfo>
                        <TrackTitle>Without Me</TrackTitle>
                        <TrackArtist>Eminem</TrackArtist>
                    </TrackInfo>
                    
                    <VolumeContainer>
                        <span style={{ fontSize: '0.8rem' }}>🔊</span>
                        <VolumeSlider
                            type="range"
                            min="0"
                            max="100"
                            step="10"
                            value={volume}
                            onChange={handleVolumeChange}
                            disabled={audioError}
                        />
                    </VolumeContainer>
                    
                    <CloseButton onClick={handleClose} title="Close music player">
                        ✕
                    </CloseButton>
                </ControlsContainer>
                
                <NoticeText>
                    {audioError 
                        ? (
                            <>
                                Audio file not found. Add your music file to /public/audio/
                                <br />
                                <button 
                                    onClick={() => setShowDebugger(true)}
                                    style={{
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.7rem',
                                        marginTop: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Debug Audio Issues
                                </button>
                            </>
                        )
                        : 'Music player - Click play after any user interaction'
                    }
                </NoticeText>
            </MusicContainer>
            
            {/* Audio Debugger - Only shown when requested */}
            {showDebugger && <AudioDebugger showByDefault={true} />}
        </AnimatePresence>
    );
};

export default BackgroundMusic; 